/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 page header (design-reference: "Travel · Bookings list", "Sales · Deal
 * record"): the page title, a one-line summary under it, and the page's
 * actions on the right. Breadcrumbs are already drawn above it by the v2
 * DashboardNavbar. Pages pass their existing action buttons unchanged.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { v2 as t, V2_DISPLAY_FONT_FAMILY } from "styles/tokens/v2";
import { V2PageHeaderActionsContext } from "components/MDBox";

/** Styles for a v2 header's action row; also used by record heroes. */
export const v2HeaderActionsSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  "& > *": { m: "0 !important" },
  // One primary action: the last filled button stays brand blue;
  // filled buttons before it read as secondary (Template, Bulk
  // upload beside Add lead), as in the mockups' headers.
  "& > .MuiButton-contained:not(:last-child), & > :not(:last-child) .MuiButton-contained": {
    background: `${t.surface} !important`,
    color: `${t.text} !important`,
    boxShadow: `inset 0 0 0 1px ${t.border2} !important`,
    "&:hover": { background: `${t.hover} !important` },
    "& svg, & .MuiIcon-root, & .material-icons-round": { color: `${t.text2} !important` },
  },
  // Record pages bring their classic dark round icon buttons (edit,
  // email, clone, delete); on the light page header they read as
  // the mockup's quiet outlined actions. Labels stay in the tooltips.
  "& .MuiIconButton-root": {
    width: 36,
    height: 36,
    padding: "0 !important",
    background: `${t.surface} !important`,
    border: `1px solid ${t.border2}`,
    borderRadius: "8px !important",
    boxShadow: "none !important",
    transform: "none !important",
    "&:hover": { background: `${t.hover} !important` },
    "&.Mui-disabled": { opacity: 0.5 },
    "& svg, & .MuiIcon-root, & .material-icons-round": { color: `${t.text2} !important`, fontSize: "20px !important" },
  },
};

export default function PageHeader({ title, summary, actions }) {
  return (
    <Box
      component="header"
      sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h1" sx={{ fontFamily: V2_DISPLAY_FONT_FAMILY, fontSize: 22, lineHeight: 1.25, fontWeight: 600, letterSpacing: "-0.01em", color: t.text, overflowWrap: "anywhere" }}>
          {title}
        </Typography>
        {summary && (
          <Typography component="div" sx={{ mt: 0.5, fontSize: 14, color: t.text2 }}>
            {summary}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box
          sx={v2HeaderActionsSx}
        >
          <V2PageHeaderActionsContext.Provider value>{actions}</V2PageHeaderActionsContext.Provider>
        </Box>
      )}
    </Box>
  );
}

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  summary: PropTypes.node,
  actions: PropTypes.node,
};

PageHeader.defaultProps = { summary: null, actions: null };
